import { cloneDeep, forEach } from "lodash";
import { createSelector } from "reselect";
import { predefinedGuides } from "../../presetShapesMeta/PredefinedGuides";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { PathOperationTypes } from "../interfaces/model/enumerations/PathOperationTypes";
import { ICustomGeometry, IPenGeometry } from "../interfaces/model/geometry/CustomGeometry";
import { IPresetGeometry } from "../interfaces/model/geometry/PresetGeometry";
import { IShapeGuide } from "../interfaces/model/geometry/ShapeGuide";
import {
	IShapePath,
} from "../interfaces/model/geometry/ShapePath";
import { IShapeTextRectangle } from "../interfaces/model/geometry/ShapeTextRectangle";
import { IXYAdjustHandle } from "../interfaces/model/geometry/XYAdjustHandle";
import { evaluateFormula } from "./guideFormulaImpl";

/**
 * Interface to denote geometry selector input data
 */
interface IData { geometry: IPresetGeometry | ICustomGeometry | IPenGeometry;
                shapeAvList: IShapeGuide[] | undefined; cy?: number | undefined; cx?: number | undefined; };

/**
 * Get geometry from data
 * @param data {IData} The input data
 */
const shapeGeometry = (data: IData): IPresetGeometry => data.geometry as IPresetGeometry;

/**
 * Get geometry from data
 * @param data {IData} The input data
 */
const shapeCustomGeometry = (data: IData): ICustomGeometry => data.geometry as ICustomGeometry;

/**
 * Get shape's own adjust value list, if available.
 * @param data {IData} The input data
 */
const shapeAvList = (data: IData): IShapeGuide[] | undefined => data.shapeAvList;

/**
 * Get shape height from data
 * @param data  {IData} The input data
 */
const shapeHeight = (data: IData): number => data.cy || 0;

/**
 * Get shape width from data
 * @param data {IData} The input data
 */
const shapeWidth = (data: IData): number => data.cx || 0;

/**
 * Get value of the input. The input can be either number or string.
 * When input is number, pass it as it is.
 * When input is a string, it would be pointing to a guide which has already
 * computed and present in computed values. Fetch and pass it.
 * @param input {number | string} The input for which value to be computed.
 * @param computedValues {Object} The object holding computed values against
 * guides.
 * @returns {number} Number value of the input value
 */
export const _getValue = (input: number | string, computedValues: any): number => {
	let v: number = NaN;
	// try parsing operand to float
	if (isNaN(input as number)) {
		// if operand is point to a guide then it would have present in accumulator in previous
		// iteration
		v = computedValues[input] as number;
	} else {
		v = parseFloat(input as string);
	}
	return v;
};

/**
 * Get angle in radian for given angle in degree
 * @param angle Angle in degree
 * @returns Angle in radian
 */
export const angleInRadian = (angle: number) => angle * Math.PI / 180.0;

/**
 * Get angle in degree for given angle in radian
 * @param angle angle in radian
 * @return Angle in degree
 */
export const angleInDegree = (angle: number) => angle * 180.0 / Math.PI;

/**
 * Get center of arc for given start point, x radius, y radius, and start angle
 * @param startX X coordinate of arc start point
 * @param startY Y coordinate of arc start point
 * @param xRadius Arc radius along x axis
 * @param yRadius Arc radius along y axis
 * @param startAngle Start angle of arc
 */
const _getArcCenter = (startX: number, startY: number, xRadius: number, yRadius: number, startAngle: number) => {
	const angleInRadians = angleInRadian(startAngle);
	const centerX = startX - (xRadius * Math.cos(angleInRadians));
	const centerY = startY - (yRadius * Math.sin(angleInRadians));
	return { centerX, centerY };
};

/**
 * Get arc end point coordinates for given arc center, x radius, y-radius and angle for the point
 * @param centerX x coordinate of arc center
 * @param centerY y coordinate of arc center
 * @param xRadius Arc radius along x axis
 * @param yRadius Arc radius along y axis
 * @param angleInDegrees Angle to which point coordinate be found
 */
export const getArcEndPoint =
    (centerX: number, centerY: number, xRadius: number, yRadius: number, angleInDegrees: number) => {
    	const angleInRadians = angleInRadian(angleInDegrees);
    	const x: number = centerX + (xRadius * Math.cos(angleInRadians));
    	const y: number = centerY + (yRadius * Math.sin(angleInRadians));
    	return { x, y };
    };

/**
 * Get arc path and end arc end point for given start point, x radius, y radius, start angle and swing angle.
 * @param startX x coordinate of arc start point
 * @param startY y coordinate of arc start point
 * @param xRadius Arc radius along x axis
 * @param yRadius Arc radius along y axis
 * @param startAngle Angle which start point makes on arc
 * @param swingAngle Swing angle to end point on arc
 */
const _getArc =
    (startX: number, startY: number, xRadius: number, yRadius: number, startAngle: number, swingAngle: number) => {
    	const endAngle = swingAngle + startAngle;

    	const { centerX, centerY } = _getArcCenter(startX, startY, xRadius, yRadius, startAngle);
    	const endPoint = getArcEndPoint(centerX, centerY, xRadius, yRadius, endAngle);

    	const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    	const d = `A ${xRadius} ${yRadius} 0 ${largeArcFlag} 1 ${endPoint.x} ${endPoint.y}`;
    	return { d, endPoint };
    };

/**
 * Computes geometry boundary meta.
 * @param boundaryMeta Geometry boundary meta to be manipulated.
 * @param xIn new x value to be manipulated
 * @param yIn new y value to be manipulated
 */
const _computeBoundaryMeta = (
	boundaryMeta: { xMax: number; xMin: number; yMax: number; yMin: number },
	xIn: number, yIn: number,
) => {
	boundaryMeta.xMin = Math.min(boundaryMeta.xMin, xIn);
	boundaryMeta.yMin = Math.min(boundaryMeta.yMin, yIn);
	boundaryMeta.xMax = Math.max(boundaryMeta.xMax, xIn);
	boundaryMeta.yMax = Math.max(boundaryMeta.yMax, yIn);
};
/**
 * Get Path string representation based on pathLst provided.
 * @param pathLst {IShapePath[]} Array of shape path
 * @param computedValues {Object} The object holding computed values against
 * guides.
 */
const _getPathString = (pathLst: IShapePath[], computedValues: any) => {
	let pathString: string = "";
	let x1: number = NaN;
	let y1: number = NaN;
	let x2: number = NaN;
	let y2: number = NaN;
	let x3: number = NaN;
	let y3: number = NaN;
	let lastPointX: number = NaN;
	let lastPointY: number = NaN;

	// Boundary meta - holds information for render-able shape dimensions WRT shape offset at origin (0,0)
	// This DOES NOT consider any transformation (position, rotation, flip) applied.
	const geoBoundaryMeta: IGeoBoundaryMeta = {
		height: 0,
		width: 0,
		xMax: 0,
		xMin: 0,
		yMax: 0,
		yMin: 0,
	};

	for (const path of pathLst) {
		for (const pathOperation of path.operations) {
			switch (pathOperation.type) {
				case PathOperationTypes.MOVE_TO:
					x1 = lastPointX = _getValue(pathOperation.pt.x, computedValues);
					y1 = lastPointY = _getValue(pathOperation.pt.y, computedValues);
					pathString = `${pathString} M${x1} ${y1} `;
					_computeBoundaryMeta(geoBoundaryMeta, x1, y1);
					break;
				case PathOperationTypes.LINE_TO:
					x1 = lastPointX = _getValue(pathOperation.pt.x, computedValues);
					y1 = lastPointY = _getValue(pathOperation.pt.y, computedValues);
					pathString = `${pathString} L ${x1} ${y1} `;
					_computeBoundaryMeta(geoBoundaryMeta, x1, y1);
					break;
				case PathOperationTypes.ARC_TO:
					// SVG format
					// A rx ry x-axis-rotation large-arc-flag sweep-flag x y

					// microsoft format
					// hr wr stAng swAng
					// last point drawn on previous operation would be start point for arc
					const arcResult = _getArc(
						lastPointX,
						lastPointY,
						_getValue(pathOperation.wR, computedValues),
						_getValue(pathOperation.hR, computedValues),
						_getValue(pathOperation.stAng, computedValues),
						_getValue(pathOperation.swAng, computedValues),
					);
					lastPointX = arcResult.endPoint.x;
					lastPointY = arcResult.endPoint.y;
					pathString = `${pathString} ${arcResult.d}`;
					_computeBoundaryMeta(geoBoundaryMeta, arcResult.endPoint.x, arcResult.endPoint.y);
					break;
				case PathOperationTypes.CUBIT_BEZ_TO:
					const controlPoint1 = pathOperation.pts[0];
					const controlPoint2 = pathOperation.pts[1];
					const ePoint = pathOperation.pts[2];

					x1 = _getValue(controlPoint1.x, computedValues);
					y1 = _getValue(controlPoint1.y, computedValues);
					_computeBoundaryMeta(geoBoundaryMeta, x1, y1);
					x2 = _getValue(controlPoint2.x, computedValues);
					y2 = _getValue(controlPoint2.y, computedValues);
					_computeBoundaryMeta(geoBoundaryMeta, x2, y2);
					x3 = lastPointX = _getValue(ePoint.x, computedValues);
					y3 = lastPointY = _getValue(ePoint.y, computedValues);
					_computeBoundaryMeta(geoBoundaryMeta, x3, y3);

					pathString = `${pathString} C ${x1} ${y1}, ${x2} ${y2}, ${x3} ${y3} `;
					break;
				case PathOperationTypes.QUAD_BEZ_TO:
					const startPoint = pathOperation.pts[0];
					const endPoint = pathOperation.pts[1];

					x1 = _getValue(startPoint.x, computedValues);
					y1 = _getValue(startPoint.y, computedValues);
					_computeBoundaryMeta(geoBoundaryMeta, x1, y1);
					x2 = lastPointX = _getValue(endPoint.x, computedValues);
					y2 = lastPointY = _getValue(endPoint.y, computedValues);
					_computeBoundaryMeta(geoBoundaryMeta, x2, y2);

					pathString = `${pathString} Q ${x1} ${y1}, ${x2} ${y2} `;
					break;
				case PathOperationTypes.CLOSE:
					pathString = `${pathString} Z `;
					break;
				default:
					break;
			}
		}
	}
	// Compute boundary meta height and width
	geoBoundaryMeta.height = geoBoundaryMeta.yMax - geoBoundaryMeta.yMin;
	geoBoundaryMeta.width = geoBoundaryMeta.xMax - geoBoundaryMeta.xMin;
	return { pathString, geoBoundaryMeta };
};

/**
 * Compute shape path based on passed geometry, shape height, and shape width.
 * Also compute adjust handlers and their position along with min max allowable values
 * @param geometry {IPresetGeometry} The shape geometry
 * @param avList {IShapeGuide[] | undefined} Shape's own adjust value list, if available
 * @param height  {number} Shape height
 * @param width  {number} Shape width
 */
const _getGeometry =
    (geometry: IPresetGeometry, avList: IShapeGuide[] | undefined, height: number, width: number):
        {
            pathInfo: { pathString: string; geoBoundaryMeta: IGeoBoundaryMeta } | undefined;
            adjHandlers: IXYAdjustHandle[];
            textRect: IShapeTextRectangle;
            avList: IShapeGuide[] | undefined;
        } => {
    	const computedValues: any = {};

    	// set the most independent values in computed values
    	computedValues.h = height;
    	computedValues.w = width;

    	// compute predefined guides
    	for (const guide of predefinedGuides) {
    		computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
    	}

    	// compute adjust value list
    	let computableAvList: IShapeGuide[] | undefined;
    	if (avList) {
    		computableAvList = avList;
    	} else if (geometry.avList) {
    		computableAvList = geometry.avList;
    	}
    	if (computableAvList) {
    		for (const guide of computableAvList) {
    			computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
    		}
    	}

    	// compute guide list
    	if (geometry.gdLst) {
    		for (const guide of geometry.gdLst) {
    			computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
    		}
    	}
    	let pathInfo;
    	if (geometry.pathLst) {
    		pathInfo = _getPathString(geometry.pathLst, computedValues);
    	}

    	// Adjust handlers
    	// For the shapes we are targeting, none is having polar adjust handlers. Hence, for now, only targeting
    	// xy based adjust handlers
    	const adjHandlers = cloneDeep(geometry.ahLst as IXYAdjustHandle[]);
    	forEach(adjHandlers, (adjustHandler) => {
    		adjustHandler.pos.x = _getValue(adjustHandler.pos.x, computedValues);
    		adjustHandler.pos.y = _getValue(adjustHandler.pos.y, computedValues);
    		if (adjustHandler.minX) {
    			adjustHandler.minX = _getValue(adjustHandler.minX, computedValues);
    		}
    		if (adjustHandler.minY) {
    			adjustHandler.minY = _getValue(adjustHandler.minY, computedValues);
    		}
    		if (adjustHandler.maxX) {
    			adjustHandler.maxX = _getValue(adjustHandler.maxX, computedValues);
    		}
    		if (adjustHandler.maxY) {
    			adjustHandler.maxY = _getValue(adjustHandler.maxY, computedValues);
    		}
    	});

    	// Text rectangle
    	const textRect = {
    		b: _getValue(geometry.rect ? geometry.rect.b : "b", computedValues),
    		l: _getValue(geometry.rect ? geometry.rect.l : "l", computedValues),
    		r: _getValue(geometry.rect ? geometry.rect.r : "r", computedValues),
    		t: _getValue(geometry.rect ? geometry.rect.t : "t", computedValues),
    	};
    	return { pathInfo, adjHandlers, textRect, avList: computableAvList };
    };

/**
 * Compute shape path based on passed geometry, shape height, and shape width.
 * Also compute adjust handlers and their position along with min max allowable values
 * @param geometry {IPresetGeometry} The shape geometry
 * @param height  {number} Shape height
 * @param width  {number} Shape width
 */
const _getCustomGeometry =
(geometry: ICustomGeometry | IPenGeometry, height: number, width: number): {
        pathInfo: any;
        adjHandlers: IXYAdjustHandle[];
        textRect: IShapeTextRectangle;
        avList: undefined;
    } => {
	const computedValues: any = {};
	// set the most independent values in computed values
	computedValues.h = height;
	computedValues.w = width;

	// compute predefined guides
	for (const guide of predefinedGuides) {
		computedValues[guide.name] = evaluateFormula(guide.fmla, computedValues);
	}

	const adjHandlers: IXYAdjustHandle[] = [];
	let pathInfo;
	if (geometry.pathLst) {
		pathInfo = _getPathString(geometry.pathLst, computedValues);
	}

	// Text rectangle
	const textRect = {
		b: _getValue(geometry.rect ? geometry.rect.b : "b", computedValues),
		l: 10,
		r: _getValue(geometry.rect ? geometry.rect.r : "r", computedValues) - 10,
		t: _getValue(geometry.rect ? geometry.rect.t : "t", computedValues),
	};
	return { pathInfo, adjHandlers, textRect, avList: undefined };
};

export const getGeometry = createSelector([shapeGeometry,
	shapeAvList, shapeHeight, shapeWidth], _getGeometry);
export const getCustomGeometry =
    createSelector([shapeCustomGeometry, shapeHeight, shapeWidth], _getCustomGeometry);
